/* Created: Admin page "Transactiemails" for all transactional mail (Brevo only).
   - Lists every mail type (order confirmations, status mails, invoice and quote mails).
   - "Voorbeeld" only shows the mail and attachment (nothing sent); "Testmail" sends one [TEST] mail to
     julan.tw@gmail.com with fictitious data; real customer mails start from orders and documents.
   - Delivery webhook status panel, the shielded Mollie test panel, and recent mail jobs with
     per-recipient delivery (customer vs business copy), bounces highlighted, and manual resolution.
   Updated: EmailJS provider switch removed, all migrated types added, delivery tracking shown.
   Updated: mail type labels come from the shared transactional-mail module. */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import Breadcrumb from '@/components/admin/Breadcrumb';
import MollieTestPanel from '@/components/admin/MollieTestPanel';
import BrevoWebhookPanel from '@/components/admin/BrevoWebhookPanel';
import { invokeBrevoMail, MAIL_TYPE_LABELS as MAIL_TYPES, TEST_MAIL_RECIPIENT } from '@/lib/transactional-mail';
import { Mail, Send, Eye, RefreshCw, Loader as Loader2, Download } from 'lucide-react';

const TRIGGER_LABEL: Record<string, string> = {
  checkout: 'Bestelling', mollie_webhook: 'Betaling', status_change: 'Statuswijziging', resend: 'Opnieuw verstuurd',
  document: 'Document', n8n: 'n8n', test: 'Test', mollie_test: 'Mollie-test',
};

const DELIVERY_STYLE: Record<string, string> = {
  accepted: 'text-[#5a6552]', deferred: 'text-amber-700', delivered: 'text-emerald-700', failed: 'text-red-700 font-semibold',
};
const DELIVERY_LABEL: Record<string, string> = {
  accepted: 'geaccepteerd', deferred: 'tijdelijk uitgesteld', delivered: 'afgeleverd', failed: 'mislukt (bounce)',
};
const ROLE_LABEL: Record<string, string> = { customer: 'Klant', business_copy: 'Kopie info@', test: 'Test', other: 'Overig' };

interface MailSetting { mail_type: string; provider: string; updated_at: string }
interface RecipientRow { recipient_email: string; role: string; delivery_state: string; state_reason: string | null; state_at: string | null }
interface MailJobRow {
  id: string; mail_type: string; mode: 'test' | 'production'; provider: string; status: string; attempts: number;
  created_at: string; accepted_at: string | null; last_error: string | null; brevo_message_id: string | null;
  recipient_email: string | null; next_attempt_at: string; trigger_source: string | null;
  orders: { order_number: string } | null; admin_documents: { document_number: string } | null;
  mail_job_recipients: RecipientRow[];
}
interface Preview { subject: string; to: string[]; html: string; attachment: { name: string; pdf_base64: string } | null }

const STATUS_STYLE: Record<string, string> = {
  accepted: 'bg-emerald-100 text-emerald-800', delivered: 'bg-emerald-200 text-emerald-900',
  queued: 'bg-sky-100 text-sky-800', retry: 'bg-amber-100 text-amber-800', processing: 'bg-sky-100 text-sky-800',
  sending: 'bg-sky-100 text-sky-800', uncertain: 'bg-orange-100 text-orange-800', failed: 'bg-red-100 text-red-800',
  bounced: 'bg-red-100 text-red-800', cancelled: 'bg-stone-200 text-stone-700',
};
const STATUS_LABEL: Record<string, string> = {
  accepted: 'Geaccepteerd door Brevo', delivered: 'Afgeleverd', queued: 'In wachtrij', retry: 'Nieuwe poging gepland',
  processing: 'In verwerking', sending: 'Wordt verzonden', uncertain: 'Onzeker - controleren', failed: 'Mislukt',
  bounced: 'Bounce', cancelled: 'Geannuleerd',
};

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '-';

export default function AdminTransactionalMail() {
  const [settings, setSettings] = useState<MailSetting[]>([]);
  const [jobs, setJobs] = useState<MailJobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirmTest, setConfirmTest] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, j] = await Promise.all([
      supabase.from('mail_type_settings').select('mail_type, provider, updated_at').order('mail_type'),
      supabase.from('mail_jobs')
        .select('id, mail_type, mode, provider, status, attempts, created_at, accepted_at, last_error, brevo_message_id, recipient_email, next_attempt_at, trigger_source, orders(order_number), admin_documents(document_number), mail_job_recipients(recipient_email, role, delivery_state, state_reason, state_at)')
        .order('created_at', { ascending: false }).limit(50),
    ]);
    if (s.error || j.error) toast.error('Mailgegevens konden niet worden geladen');
    setSettings((s.data as MailSetting[]) ?? []);
    setJobs((j.data as unknown as MailJobRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handlePreview(mailType: string) {
    setBusy(`preview:${mailType}`);
    try {
      const data = await invokeBrevoMail<Preview>({ action: 'preview', mail_type: mailType });
      if (typeof data.html !== 'string' || !Array.isArray(data.to)) throw new Error('Onvolledig voorbeeld ontvangen');
      setPreview({ ...data, attachment: data.attachment?.pdf_base64 ? data.attachment : null });
    } catch (e) {
      toast.error('Voorbeeld laden mislukt', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusy(null);
    }
  }

  async function handleSendTest(mailType: string) {
    setConfirmTest(null);
    setBusy(`test:${mailType}`);
    try {
      const data = await invokeBrevoMail<{ job?: MailJobRow }>({ action: 'send_test', mail_type: mailType });
      const job = data.job;
      if (!job) throw new Error('Geen resultaat ontvangen');
      if (job.status === 'accepted') toast.success('Testmail geaccepteerd door Brevo', { description: `Controleer de inbox van ${job.recipient_email}.` });
      else toast.warning(`Testmail: ${STATUS_LABEL[job.status] ?? job.status}`, { description: job.last_error ?? undefined });
    } catch (e) {
      toast.error('Testmail mislukt', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusy(null);
      load();
    }
  }

  async function handleResolve(jobId: string, action: 'mark_accepted' | 'requeue' | 'cancel') {
    setBusy(`resolve:${jobId}`);
    const { error } = await supabase.rpc('resolve_mail_job', { p_job_id: jobId, p_action: action });
    setBusy(null);
    if (error) toast.error('Actie mislukt', { description: error.message });
    load();
  }

  function downloadPdf() {
    if (!preview?.attachment) return;
    const { name, pdf_base64 } = preview.attachment;
    const bytes = Uint8Array.from(atob(pdf_base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 space-y-6">
      <Breadcrumb pageName="Transactiemails" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold text-[#1e2b22] mb-2">Transactiemails</h1>
          <p className="text-[#5a6552] max-w-2xl">
            Alle bestel-, status- en documentmails gaan via Brevo met een kopie naar info@buitnplanten.nl.
            <strong className="font-semibold"> Voorbeeld</strong> verstuurt niets.
            <strong className="font-semibold"> Testmail</strong> gaat alleen naar {TEST_MAIL_RECIPIENT} met fictieve gegevens (maximaal 10 per uur).
            Echte klantmails ontstaan alleen vanuit bestellingen en documenten.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="border-[#e7e1d6]">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Vernieuwen
        </Button>
      </div>

      <Card className="border border-[#e7e1d6] bg-white">
        <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
          <CardTitle className="flex items-center gap-2 text-[#1e2b22]"><Mail className="w-5 h-5" />Mailtypes</CardTitle>
        </CardHeader>
        <CardContent className="p-0 divide-y divide-[#e7e1d6]">
          {settings.map((s) => (
            <div key={s.mail_type} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6">
              <div>
                <p className="font-semibold text-[#1e2b22]">{MAIL_TYPES[s.mail_type] ?? s.mail_type}</p>
                <p className="text-sm text-[#5a6552] mt-1">Productie via Brevo, kopie naar info@buitnplanten.nl</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="border-[#e7e1d6]" disabled={!!busy} onClick={() => handlePreview(s.mail_type)}>
                  {busy === `preview:${s.mail_type}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}Alleen voorbeeld
                </Button>
                <Button className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white" disabled={!!busy} onClick={() => setConfirmTest(s.mail_type)}>
                  {busy === `test:${s.mail_type}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Testmail naar mij
                </Button>
              </div>
            </div>
          ))}
          {!loading && settings.length === 0 && <p className="p-6 text-[#5a6552]">Geen mailtypes gevonden.</p>}
        </CardContent>
      </Card>

      <BrevoWebhookPanel />

      <MollieTestPanel onChanged={load} />

      <Card className="border border-[#e7e1d6] bg-white">
        <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
          <CardTitle className="text-[#1e2b22]">Recente mailopdrachten</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Aangemaakt</TableHead><TableHead>Soort</TableHead><TableHead>Mail</TableHead><TableHead>Bestelling / document</TableHead>
                <TableHead>Status</TableHead><TableHead>Pogingen</TableHead><TableHead>Details</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((j) => (
                <TableRow key={j.id}>
                  <TableCell className="whitespace-nowrap">{formatDateTime(j.created_at)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={j.mode === 'test' ? 'border-amber-300 text-amber-800' : 'border-emerald-300 text-emerald-800'}>
                      {j.mode === 'test' ? 'Test' : 'Echte klantmail'}
                    </Badge>
                    {j.trigger_source && <div className="text-xs text-[#5a6552] mt-1">{TRIGGER_LABEL[j.trigger_source] ?? j.trigger_source}</div>}
                  </TableCell>
                  <TableCell className="text-sm max-w-[14rem]">
                    {MAIL_TYPES[j.mail_type] ?? j.mail_type}
                    {j.provider !== 'brevo' && <div className="text-xs text-[#5a6552]">via {j.provider}</div>}
                  </TableCell>
                  <TableCell>{j.admin_documents?.document_number ?? j.orders?.order_number ?? (j.mode === 'test' ? 'Fictief' : '-')}</TableCell>
                  <TableCell><span className={`text-xs font-medium px-2 py-1 rounded ${STATUS_STYLE[j.status] ?? ''}`}>{STATUS_LABEL[j.status] ?? j.status}</span></TableCell>
                  <TableCell>{j.attempts}</TableCell>
                  <TableCell className="text-xs text-[#5a6552] max-w-xs">
                    {j.mail_job_recipients?.length > 0 ? j.mail_job_recipients.map((r) => (
                      <div key={r.recipient_email} className={DELIVERY_STYLE[r.delivery_state] ?? ''}>
                        {ROLE_LABEL[r.role] ?? r.role}: {r.recipient_email} - {DELIVERY_LABEL[r.delivery_state] ?? r.delivery_state}
                        {r.state_reason && r.delivery_state !== 'delivered' && <span className="block break-words">{r.state_reason}</span>}
                      </div>
                    )) : j.recipient_email && <div>Aan: {j.recipient_email}</div>}
                    {j.status === 'bounced' && <div className="text-red-700">Niet automatisch opnieuw verstuurd. Controleer het adres en verstuur bewust opnieuw vanuit de bestelling.</div>}
                    {j.accepted_at && <div>Geaccepteerd: {formatDateTime(j.accepted_at)}</div>}
                    {j.status === 'retry' && <div>Volgende poging: {formatDateTime(j.next_attempt_at)}</div>}
                    {j.last_error && <div className="text-red-700 break-words">{j.last_error}</div>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap space-x-2">
                    {j.status === 'uncertain' && (
                      <Button size="sm" variant="outline" disabled={!!busy} onClick={() => handleResolve(j.id, 'mark_accepted')}>Wel ontvangen</Button>
                    )}
                    {(j.status === 'uncertain' || j.status === 'failed') && (
                      <Button size="sm" variant="outline" disabled={!!busy} onClick={() => handleResolve(j.id, 'requeue')}>Opnieuw</Button>
                    )}
                    {['queued', 'retry', 'failed', 'uncertain'].includes(j.status) && (
                      <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => handleResolve(j.id, 'cancel')}>Annuleren</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && jobs.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-[#5a6552] py-8">Nog geen mailopdrachten.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmTest} onOpenChange={(o) => !o && setConfirmTest(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Testmail versturen?</AlertDialogTitle>
            <AlertDialogDescription>
              Er gaat precies één mail met [TEST] in het onderwerp en fictieve klantgegevens naar {TEST_MAIL_RECIPIENT}, zonder kopie naar info@. Klanten ontvangen niets.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmTest && handleSendTest(confirmTest)}>Versturen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{preview?.subject}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5a6552]">Alleen voorbeeld, er is niets verzonden. Bij een testmail: aan {preview?.to.join(', ')}.</p>
          <iframe title="Voorbeeld" sandbox="" srcDoc={preview?.html} className="w-full h-[60vh] border border-[#e7e1d6] rounded" />
          {preview?.attachment ? (
            <Button variant="outline" onClick={downloadPdf} className="self-start border-[#e7e1d6]">
              <Download className="w-4 h-4 mr-2" />{preview.attachment.name} downloaden
            </Button>
          ) : (
            <p className="text-sm text-[#5a6552]">Dit mailtype heeft geen bijlage.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
